import { Box, Button, Flex, IconButton, Menu, Portal, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useState } from 'react'
import {
  LuCircleHelp,
  LuCircleUserRound,
  LuDatabase,
  LuGauge,
  LuKeyRound,
  LuLogOut,
  LuMessageSquare,
  LuShieldCheck,
  LuUser,
} from 'react-icons/lu'
import { Link as RouterLink, Outlet, useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useMe } from '~/api/hooks'
import { DashboardShell, type ShellNavItem } from '~/components/shell/DashboardShell'
import { HelpProvider, MemberLayout, useHelp } from '~/components/member/layout'
import { MiniFooter } from '~/components/MiniFooter'
import { OrgMark } from '~/components/site'
import { ThemeControl } from '~/components/ThemeControl'
import { ErrorAlert } from '~/components/member/feedback'
import { useT } from '~/i18n/use-t'
import { dropPrivateSession } from '~/lib/session'

/**
 * Sign out (passwordless P3: it ends every session of the voter, on every device). The page leaves only once the
 * server confirmed it: a 401 means the session was already gone, which is the same thing. Any other failure keeps the
 * voter here with a visible "not signed out yet" and a Retry.
 */
function useLogoutMutation() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        await api('memberLogout')
      } catch (err) {
        if (!(isApiError(err) && err.status === 401)) throw err
      }
    },
    onSuccess: () => {
      dropPrivateSession(qc)
      navigate('/login')
    },
  })
}
type Logout = ReturnType<typeof useLogoutMutation>
const LogoutContext = createContext<Logout | null>(null)
const useLogoutContext = () => useContext(LogoutContext)!
/** The shell's sign-out (the "Sign-in" section has its own button for it); null outside the voter shell. */
export const useLogout = () => useContext(LogoutContext)

/** The failed logout, over the page and the tab bar so it is seen wherever the voter pressed Sign out. */
function LogoutFailed({ logout }: { logout: Logout }) {
  const { t } = useT()
  if (!logout.isError) return null
  return (
    <Box
      position='fixed'
      insetInline={3}
      bottom={{ base: 'calc(5rem + env(safe-area-inset-bottom, 0px))', lg: 4 }}
      zIndex='toast'
      maxW='md'
      mx='auto'
    >
      <Stack gap={2} bg='bg' borderRadius='md' shadow='lg'>
        <ErrorAlert>{t('dashboard.logOutFailed')}</ErrorAlert>
        <Button variant='outline' loading={logout.isPending} onClick={() => logout.mutate()}>
          {t('dashboard.logOutRetry')}
        </Button>
      </Stack>
    </Box>
  )
}

/**
 * The foot of the sidebar: the member's name and the theme, Help, Sign out, then the mini footer (design spec §5). The
 * account menu of the top bar holds the same under `lg`, so the page itself carries no Help button of its own.
 */
function SidebarFooter() {
  const { t } = useT()
  const me = useMe()
  const logout = useLogoutContext()
  const help = useHelp()
  const name = me.data?.member?.name
  return (
    <Stack gap={1} pt={3} pb={1} borderTop='1px solid' borderColor='border'>
      <Flex align='center' justify='space-between' gap={2}>
        <Text fontSize='sm' lineClamp={2} overflowWrap='anywhere' minW={0} ps={2} title={name ?? undefined}>
          {name}
        </Text>
        <ThemeControl size='xs' />
      </Flex>
      <Button size='sm' variant='ghost' alignSelf='flex-start' px={2} minH={12} onClick={() => help.open()}>
        <LuCircleHelp aria-hidden />
        {t('common.help')}
      </Button>
      {me.data && (
        <Button
          size='sm'
          variant='ghost'
          alignSelf='flex-start'
          px={2}
          minH={12}
          loading={logout.isPending}
          onClick={() => logout.mutate()}
        >
          <LuLogOut aria-hidden />
          {t('dashboard.logOut')}
        </Button>
      )}
      <MiniFooter />
    </Stack>
  )
}

/** "Your data is deleted" (DeleteMyData): the shell drops the member menu and shows the confirmation alone. */
export interface Erased {
  hadCheck: boolean
}
export const ErasedContext = createContext<{ erased: Erased | null; setErased: (e: Erased) => void }>({
  erased: null,
  setErased: () => {},
})
export const useErased = () => useContext(ErasedContext)

/**
 * The account menu of the phone and tablet top bar (mobile review, ruling M3; design spec 2026-10-30 §5): the
 * member's name, Help, Privacy Policy and Sign out, behind one 48 px button. The sidebar shows the same things from `lg`.
 */
function AccountMenu({ onHelp }: { onHelp: () => void }) {
  const { t } = useT()
  const me = useMe()
  const logout = useLogoutContext()
  const name = me.data?.member?.name
  const item = { minH: '12', px: 3, gap: 3, cursor: 'pointer' } as const
  return (
    <Menu.Root positioning={{ placement: 'bottom-end' }}>
      <Menu.Trigger asChild>
        <IconButton variant='ghost' flexShrink={0} minW='12' minH='12' aria-label={t('memberNav.account')}>
          <LuCircleUserRound aria-hidden />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW='56' maxW='calc(100vw - 2rem)'>
            {name && (
              <Menu.ItemGroup>
                <Menu.ItemGroupLabel fontWeight='normal' color='fg.muted' lineClamp={2} overflowWrap='anywhere'>
                  {name}
                </Menu.ItemGroupLabel>
              </Menu.ItemGroup>
            )}
            <Menu.Item value='help' {...item} onClick={onHelp}>
              <LuCircleHelp aria-hidden />
              {t('common.help')}
            </Menu.Item>
            <Menu.Item value='privacy' {...item} asChild>
              <RouterLink to='/privacy'>
                <LuShieldCheck aria-hidden />
                {t('footer.privacyPolicy')}
              </RouterLink>
            </Menu.Item>
            {me.data && (
              <>
                <Menu.Separator />
                <Menu.Item value='logout' {...item} onClick={() => logout.mutate()}>
                  <LuLogOut aria-hidden />
                  {t('dashboard.logOut')}
                </Menu.Item>
              </>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

/**
 * The frame of the member pages (`/me/*`). From `lg` (1024 px) the same sidebar as the admin area; below it a compact
 * top bar (the organization's mark, the theme and the account menu) and a bottom tab bar with the five pages (mobile
 * review, ruling M3: a phone held sideways and a tablet get the phone layout too). It has no 401 redirect of its own
 * (the page that erased the data must still show its confirmation); every section page guards itself. A member
 * without a `member` (a "not accepted" or "do the face check again" card) sees the Overview only. Once the data is
 * erased the page shows the confirmation alone, with no member menu.
 */
export default function MemberShell() {
  const { t } = useT()
  const me = useMe()
  const [erased, setErased] = useState<Erased | null>(null)
  const logout = useLogoutMutation()
  const items: ShellNavItem[] = [
    { to: '/me', label: t('memberNav.overview'), short: t('memberNav.tabs.overview'), icon: LuGauge, end: true },
  ]
  if (me.data?.member)
    items.push(
      { to: '/me/profile', label: t('memberNav.profile'), short: t('memberNav.tabs.profile'), icon: LuUser },
      { to: '/me/contact', label: t('memberNav.contact'), short: t('memberNav.tabs.contact'), icon: LuMessageSquare },
      { to: '/me/sign-in', label: t('memberNav.signIn'), short: t('memberNav.tabs.signIn'), icon: LuKeyRound },
      { to: '/me/data', label: t('memberNav.data'), short: t('memberNav.tabs.data'), icon: LuDatabase }
    )
  if (erased)
    return (
      <ErasedContext.Provider value={{ erased, setErased }}>
        <MemberLayout account={false}>
          <Outlet />
        </MemberLayout>
      </ErasedContext.Provider>
    )
  return (
    <ErasedContext.Provider value={{ erased, setErased }}>
      <LogoutContext.Provider value={logout}>
        <LogoutFailed logout={logout} />
        <HelpProvider>
          {(openHelp) => (
            <DashboardShell
              touch
              sidebarFrom='lg'
              mobileNav='tabs'
              header={
                <Stack px={2} pt={3}>
                  <OrgMark />
                </Stack>
              }
              items={items}
              navLabel={t('memberNav.label')}
              openMenuLabel={t('memberNav.openMenu')}
              footer={<SidebarFooter />}
              mobileBar={
                <>
                  <Box flex='1' minW={0}>
                    <OrgMark markOnlyWhenNarrow />
                  </Box>
                  <ThemeControl />
                  <AccountMenu onHelp={openHelp} />
                </>
              }
            />
          )}
        </HelpProvider>
      </LogoutContext.Provider>
    </ErasedContext.Provider>
  )
}
